import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import LearnMore from "@/components/LearnMore";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Target, Mic, Square, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Missions() {
  const { refreshUser } = useAuth();
  const [mission, setMission] = useState(null);
  const [reflection, setReflection] = useState("");
  const [reply, setReply] = useState(null);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { api.get("/missions/today").then((r) => setMission(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!reflection.trim()) { toast.error("Tell Xcelio what happened first."); return; }
    setSending(true);
    try { const { data } = await api.post("/missions/return", { text: reflection }); setReply(data.reply); await refreshUser(); }
    catch { toast.error("Could not save your reflection."); } finally { setSending(false); }
  };

  const startRec = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream); mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" }); const fd = new FormData(); fd.append("file", blob, "a.webm");
        try { const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data.text) setReflection((p) => (p ? p + " " : "") + data.text); }
        catch { toast.error("Couldn't hear that."); }
      };
      mr.start(); setRecording(true);
    } catch { }
  };
  const stopRec = () => { mrRef.current?.stop(); setRecording(false); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Real-world mission</p>
          <h1 className="font-serif text-4xl sm:text-5xl">Back into life.</h1>
        <div className="mb-4"><LearnMore area="missions" /></div>
          <p className="text-muted-foreground mt-2">Xcelio isn't somewhere to hide. It's the doorway back into the world.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-border p-8 mb-8 bg-card/50">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary mb-3"><Target className="w-4 h-4" /> Today's mission</div>
          {mission ? (
            <>
              <p className="font-serif text-3xl leading-snug mb-2" data-testid="mission-text">{mission.text}</p>
              {mission.completed && <div className="flex items-center gap-1 text-sm text-primary mt-2"><Check className="w-4 h-4" /> Completed</div>}
            </>
          ) : <Loader2 className="w-5 h-5 animate-spin text-primary" />}
        </motion.div>

        <div className="rounded-2xl border border-border p-6">
          <h3 className="font-serif text-2xl mb-1">Come back and tell Xcelio</h3>
          <p className="text-sm text-muted-foreground mb-4">How did it go? When did you feel most like yourself?</p>
          <div className="relative">
            <Textarea value={reflection} onChange={(e) => setReflection(e.target.value)} rows={4} placeholder="What happened…" data-testid="mission-reflection" className="pr-12" />
            <button type="button" onClick={recording ? stopRec : startRec} data-testid="mission-mic" className={`absolute bottom-3 right-3 p-2 rounded-full ${recording ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}>{recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</button>
          </div>
          <Button className="w-full rounded-full mt-4" onClick={submit} disabled={sending} data-testid="mission-submit">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tell Xcelio"}</Button>

          {reply && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl glass p-5" data-testid="mission-reply">
              <div className="text-xs uppercase tracking-[0.18em] text-primary mb-2">Xcelio</div>
              <p className="font-serif text-xl leading-relaxed">{reply}</p>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
